'use client';

import { isClient } from '@fe-common-utils/libs/utils';
import { styled } from '@linaria/react';
import { CSSProperties, useEffect, useRef } from 'react';

interface ExtendedCSSProperties extends CSSProperties {
  '--animation-duration'?: string;
  '--animation-name'?: string;
  '--animation-delay'?: string;
}

const View = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  min-width: 81px;
  overflow: hidden;
  min-height: 20px;

  .index-carousel {
    width: 100%;
    height: 500%;
    position: absolute;
    top: 0;
    left: 0;
    animation: linear infinite;
    animation-duration: var(--animation-duration);
    animation-name: var(--animation-name);
    animation-delay: var(--animation-delay);

    > li {
      height: 20%;
      display: flex;
      width: 100%;
      align-items: center;
    }
  }
`;

const generateKeyframes = ({
  carouselNumber,
  transitionSeconds = 3,
  stopSeconds = 1,
  animationName = 'carousel-keyframesName'
}: {
  carouselNumber: number;
  transitionSeconds?: number;
  stopSeconds?: number;
  animationName?: string;
}) => {
  const perNumNeedSec = transitionSeconds + stopSeconds;
  const sumSec = carouselNumber * perNumNeedSec;
  const frame = 100 / sumSec;

  let accTime = 0;
  const keyframesStr = Array.from(Array(carouselNumber)).reduce((result, _, index) => {
    const topOffsetVolume = (index + 1) * -100;
    const statTransitionTime = accTime + frame;
    const stopTime = statTransitionTime + stopSeconds * frame;

    accTime += perNumNeedSec * frame;

    return `${result} ${statTransitionTime}% {top:${topOffsetVolume}%;} ${stopTime}% {top:${topOffsetVolume}%;}`;
  }, '');

  return `@keyframes ${animationName} {0% { top: 0px; } ${keyframesStr}`;
};

const Flip = ({
  carouselNumber = 2,
  children,
  itemClassName = 'flip-item',
  bodyClassName = 'flip-container',
  stopSeconds = 3,
  transitionSeconds = 1,
  animationName = 'carousel-keyframesName',
  delayPlaySeconds = 0
}: {
  carouselNumber: number;
  bodyClassName?: string;
  children: React.ReactNode;
  itemClassName?: string;
  stopSeconds?: number;
  transitionSeconds?: number;
  animationName?: string;
  delayPlaySeconds?: number;
}) => {
  const styleTagRef = useRef<HTMLStyleElement | null>(null);
  const childrenArray = Array.isArray(children) ? children : [children];
  const childrenIndexes = childrenArray.length ? [...childrenArray, childrenArray[0]] : [];

  useEffect(() => {
    if (isClient) {
      // 創建一個styleSheet，依據資料數去動態改變keyframes
      const styleSheet = document.createElement('style');
      styleSheet.textContent = generateKeyframes({
        carouselNumber,
        transitionSeconds,
        stopSeconds,
        animationName
      });
      document.head.appendChild(styleSheet);
      styleTagRef.current = styleSheet;
    }

    return () => {
      if (styleTagRef.current) {
        document.head.removeChild(styleTagRef.current);
      }
    };
  }, [carouselNumber]);

  return (
    <View className={bodyClassName}>
      <ul
        className='index-carousel'
        style={
          {
            '--animation-duration': `${(carouselNumber * stopSeconds * transitionSeconds).toString()}s`,
            '--animation-name': animationName,
            '--animation-delay': `${delayPlaySeconds ? delayPlaySeconds : carouselNumber}s`
          } as ExtendedCSSProperties
        }
      >
        {childrenIndexes.map((child, index: number) => {
          return (
            <li className={itemClassName} key={index}>
              {child}
            </li>
          );
        })}
      </ul>
    </View>
  );
};

export default Flip;
